/**
 * useQuestCats — справочники экрана «Квесты и слоты», которых нет в
 * useAppData: consumables (глобальные строки A33) / resources /
 * quest_resources / quest_prices / quest-versions (A35). Свой fetch с
 * refetch по tick (бампится хостом после мутаций). Нормы расходников
 * (quest_consumables) — ленивый per-quest кеш consOf (fetch на раскрытие
 * карточки), setConsOf/setCats отданы хосту под optimistic-апдейты.
 */
import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { api } from "../api/client";
import type {
  ApiList, Consumable, QuestConsumable, QuestPrice, QuestResource,
  QuestVersion, Resource,
} from "../api/types";

/** Нормы квеста в wire-формате bulk (без скоупа quest_id/version_id). */
export const consWire = (rows: QuestConsumable[]) =>
  rows.map((c) => ({ consumable_id: c.consumable_id, qty: c.qty, per_player: c.per_player }));

export interface QuestCats {
  cons: Consumable[];
  res: Resource[];
  questRes: Record<string, string[]>;
  prices: Record<string, QuestPrice>;
  versions: QuestVersion[];
  loaded: boolean;
}

export const CATS0: QuestCats = {
  cons: [], res: [], questRes: {}, prices: {}, versions: [], loaded: false,
};

export interface QuestCatsHook {
  cats: QuestCats;
  setCats: Dispatch<SetStateAction<QuestCats>>;
  consOf: Record<string, QuestConsumable[]>;
  setConsOf: Dispatch<SetStateAction<Record<string, QuestConsumable[]>>>;
  loadCons: (questId: string) => void;
  /** Нормы квеста ЯВНЫМ GET (кеш — только фолбэк сетевой ошибки): для путей,
      где race с ленивым loadCons терял бы данные (клон, prefill привязки). */
  consFor: (questId: string) => Promise<QuestConsumable[]>;
  /** Pull-to-refresh: перезапросить уже загруженные скоупы норм заново —
      сам loadCons-эффект карточки не перезапустится (deps без изменений). */
  resetCons: () => void;
}

export function useQuestCats(branchId: string, tick: number): QuestCatsHook {
  const [cats, setCats] = useState<QuestCats>(CATS0);
  const [consOf, setConsOf] = useState<Record<string, QuestConsumable[]>>({});
  const reqCons = useRef(new Set<string>());
  const consRef = useRef(consOf);
  consRef.current = consOf;

  useEffect(() => {
    if (!branchId) return;
    let dead = false;
    void Promise.all([
      api<ApiList<Consumable>>("/api/consumables"),
      api<ApiList<Resource>>("/api/resources"),
      api<ApiList<QuestResource>>("/api/quest-resources"),
      api<ApiList<QuestPrice>>("/api/prices/quests"),
      api<ApiList<QuestVersion>>(`/api/quest-versions?branch=${encodeURIComponent(branchId)}`),
    ]).then(([c, r, qr, pr, vs]) => {
      if (dead) return;
      const questRes: Record<string, string[]> = {};
      for (const x of qr.results ?? []) (questRes[x.quest_id] ??= []).push(x.resource_id);
      const prices: Record<string, QuestPrice> = {};
      for (const p of pr.results ?? []) prices[p.quest_id] = p;
      setCats({
        cons: (c.results ?? []).filter((x) => x.quest_id == null && x.active !== 0),
        res: r.results ?? [], questRes, prices, versions: vs.results ?? [], loaded: true,
      });
    }).catch(() => { if (!dead) setCats((p) => ({ ...p, loaded: true })); });
    return () => { dead = true; };
  }, [branchId, tick]);

  const loadCons = useCallback((questId: string) => {
    if (reqCons.current.has(questId)) return;
    reqCons.current.add(questId);
    void api<ApiList<QuestConsumable>>(`/api/quest-consumables?quest=${encodeURIComponent(questId)}`)
      .then((r) => setConsOf((m) => ({ ...m, [questId]: r.results ?? [] })))
      .catch(() => { reqCons.current.delete(questId); });
  }, []);

  const consFor = useCallback((questId: string): Promise<QuestConsumable[]> =>
    api<ApiList<QuestConsumable>>(`/api/quest-consumables?quest=${encodeURIComponent(questId)}`)
      .then((r) => {
        const rows = r.results ?? [];
        setConsOf((m) => ({ ...m, [questId]: rows }));
        return rows;
      })
      .catch(() => consRef.current[questId] ?? []), []);

  const resetCons = useCallback(() => {
    const ids = [...reqCons.current];
    reqCons.current.clear();
    for (const id of ids) loadCons(id);
  }, [loadCons]);

  return { cats, setCats, consOf, setConsOf, loadCons, consFor, resetCons };
}
